import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CardContent,
  useTheme,
  MenuItem,
  Select,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { nativeDateFieldProps } from "../Components/CalendarDatePicker";

const AddExpenses = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState({
    bill_id: "",
    description: "",
    amount: "",
    branch_id: "",
    date: new Date().toISOString().split("T")[0],
  });
  const token = localStorage.getItem("token");
  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        "https://api.clever-school.com/api/expenses",
        {
          ...formData,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(t("expenseAdded"));
      navigate("/GetExpenses");
    } catch (error) {
      message.error(t("expenseAddError"));
      console.error("Submission error:", error);
    }
  };

  const handleReset = () => {
    setFormData({
      bill_id: "",
      description: "",
      amount: "",
    });
  };

  const handleChange = (name) => (e) => {
    setFormData({ ...formData, [name]: e.target.value });
  };
  const [userRole, setUserRole] = React.useState("");
  // eslint-disable-next-line no-unused-vars
  const [userBranch, setUserBranch] = React.useState("");

  React.useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role);
        setUserBranch(user.branch_id);

        // Fixed: Update formData instead of non-existent setBranch_id
        if (user.role !== "superadmin") {
          setFormData((prev) => ({
            ...prev,
            branch_id: user.branch_id.toString(),
          }));
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);
  const formFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: "none",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  const formSelect = {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": {
      border: "none",
      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };
  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
        direction,
        textAlign,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("addExpenses")}
          </Typography>
          <Grid container spacing={3}>
            {/* Bill ID Field */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("billId")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.bill_id}
                  onChange={handleChange("bill_id")}
                  placeholder={t("enterBillId")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>
            {/* Date Field */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("date")}
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={formData.date}
                  onChange={handleChange("date")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...nativeDateFieldProps()}
                />
              </CardContent>
            </Grid>
            {userRole === "superadmin" && (
              <Grid item xs={12} md={6}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t("branch")}
                  </Typography>
                  <Select
                    fullWidth
                    value={formData.branch_id}
                    onChange={handleChange("branch_id")}
                    variant="outlined"
                    displayEmpty
                    required
                    sx={formSelect}
                  >
                    <MenuItem value="" disabled>
                      {t("selectBranch")}
                    </MenuItem>
                    <MenuItem value={1}>{t("branch1.1")}</MenuItem>
                    <MenuItem value={2}>{t("branch1.2")}</MenuItem>
                    <MenuItem value={3}>{t("branch1.3")}</MenuItem>
                  </Select>
                </CardContent>
              </Grid>
            )}

            {/* Amount Field */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("amount")}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.amount}
                  onChange={handleChange("amount")}
                  placeholder={t("enterAmount")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                  InputProps={
                    {
                      // startAdornment: (
                      //   <Typography variant="body2" sx={{ mr: 1 }}>
                      //     {t("currencySymbol")}
                      //   </Typography>
                      // ),
                    }
                  }
                />
              </CardContent>
            </Grid>

            {/* Description Field */}
            <Grid item xs={12}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("description")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.description}
                  onChange={handleChange("description")}
                  placeholder={t("enterExpenseDescription")}
                  variant="outlined"
                  multiline
                  sx={formFieldStyles}
                  rows={4}
                />
              </CardContent>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    textTransform: "none",
                    color: theme.palette.info.main,
                    borderColor: theme.palette.info.main,
                  }}
                >
                  {t("reset")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/GetExpenses")}
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    textTransform: "none",
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    px: 6,
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: theme.palette.success.main,
                    "&:hover": {
                      backgroundColor: theme.palette.success.dark,
                    },
                  }}
                >
                  {t("submit")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddExpenses;
